import React, { useContext, useState, useEffect } from 'react';
import Api from 'api';
import { storage, observer } from 'store';
import { FormGrid, FormField, FormRow, FormColumn, FormSubmit } from 'components/forms';
import { Input } from 'components/UI';
import withForm from 'components/forms/withForm';
import { ErrorsShakeHubContext, findFirstFieldWithError } from 'components/forms/utils';
import { corporateFormSchema } from 'components/forms/validationSchemas';
import { pushCustomSubmitEvent } from 'utils';
import { getCountryCode } from 'utils/localised';
import { phoneCode } from 'define/locale';

import * as styles from './CorporateForm.module.scss';

const Form = ({
  handleSubmit,
  fields,
  isValid,
  values,
  errors,
  isSubmitting,
  status,
  setStatus,
  formName,
  id,
  className,
  AdditionalInformationForForm,
  submitTitle,
  fullWidthButton,
  buttonClassName,
}) => {
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const [btnPressed, setBtnPressed] = useState(false);

  useEffect(() => {
    return () => storage.addItem('isEmailCheckedSuccessful', false);
  }, []);

  useEffect(() => {
    if (!isValid && btnPressed) {
      errorsShakeHub.dispatchEvent(new CustomEvent('shakeError'));
      errorsShakeHub.dispatchEvent(
        new CustomEvent('setFocus', {
          detail: findFirstFieldWithError(values, errors),
        }),
      );
      setBtnPressed(false);
    }
  }, [isValid, btnPressed, errorsShakeHub, values, errors]);

  useEffect(() => {
    if (status === 'success') {
      setStatus();
    }
  }, [status, setStatus]);

  return (
    <FormGrid
      submitHandler={e => {
        setBtnPressed(true);
        handleSubmit(e);
      }}
      formName={formName}
      id={id}
      className={className}
    >
      <FormRow sizes={['sm12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.name} />
          <FormField
            Component={Input}
            {...fields.email}
            isLoading={storage.getItem('isEmailCheckLoading')}
            isChecked={storage.getItem('isEmailCheckedSuccessful')}
          />
          <FormField Component={Input} {...fields.phone} />
          <FormField Component={Input} {...fields.company} />
          <FormField Component={Input} {...fields.message} />
          <FormField Component={Input} {...fields.linkedIn} />
        </FormColumn>
      </FormRow>
      {AdditionalInformationForForm}
      <div className={styles.submitWrap}>
        <FormSubmit
          title={submitTitle ?? 'Register'}
          nda={fields.nda}
          photoConsent={fields.photoConsent}
          disabled={isSubmitting}
          fullWidth={fullWidthButton}
          buttonClassName={buttonClassName}
          size="sm"
        />
      </div>
    </FormGrid>
  );
};

export default function CorporateForm({
  id = 'executives-breakfast-ai-frisco-november',
  formName = 'executives-breakfast-ai-frisco-november',
  closeModal,
  onSuccess,
  additionalFormData,
  className,
  getAdditionalFormData,
  AdditionalInformationForForm = '',
  inModal = false,
  withNda = true,
  withPhotoConsent = true,
  fullWidthButton,
  buttonClassName,
  submitTitle,
}) {
  const fields = {
    name: {
      initialValue: '',
      domProps: {
        placeholder: 'Full name *',
        maxLength: 255,
      },
    },
    email: {
      initialValue: '',
      inModal,
      domProps: {
        placeholder: 'Corporate email *',
        type: 'email',
        maxLength: 254,
      },
    },
    phone: {
      initialValue: '',
      getInitialValue,
      mask: {
        mask: '+99999999999999999',
        maskChar: '',
        formatChars: {
          9: '[0-9]',
        },
      },
      domProps: {
        placeholder: 'Phone number *',
        type: 'tel',
      },
    },
    company: {
      initialValue: '',
      domProps: {
        maxLength: 255,
        placeholder: 'Company name *',
      },
    },
    message: {
      initialValue: '',
      domProps: {
        maxLength: 500,
        placeholder: 'Position in the company *',
      },
    },
    linkedIn: {
      initialValue: '',
      domProps: {
        maxLength: 128,
        placeholder: 'LinkedIn profile',
      },
    },
  };

  if (withNda) {
    fields.nda = {
      initialValue: false,
      inModal,
      withoutError: true,
      content: 'I want to keep my personal data private.',
    };
  }

  if (withPhotoConsent) {
    fields.photoConsent = {
      initialValue: false,
      inModal,
      withoutError: true,
      content: 'I consent to having my photographs taken and used in promotional materials.',
    };
  }

  const errorsMessages = {
    name: 'Please enter your full name',
    email: {
      required: 'Please enter your corporate email',
      notCorporateEmail: 'Please enter your corporate email',
    },
    phone: 'Please enter your phone number',
    company: 'Please enter the name of the company you represent',
    message: 'Please enter your position',
    linkedIn: {
      checkUrl: 'Enter a valid LinkedIn URL',
      required: 'Enter your LinkedIn profile',
    },
    emoji: 'Emojis are not allowed',
    specialSymbols: 'Special characters are not allowed',
  };

  const defaultOnSuccess = () => storage.addItem('isEmailCheckedSuccessful', false);

  const Component = withForm({
    fields,
    formName,
    id,
    className,
    AdditionalInformationForForm,
    submitTitle,
    fullWidthButton,
    buttonClassName,
    validation: props => corporateFormSchema(errorsMessages, props),
    submit: {
      id,
      formName,
      handler: Api.sendForm.bind(Api, 'integration/form-data'),
      reset: true,
      successModal: 'SuccessModal',
      errorModal: 'ErrorModal',
      onSuccess: onSuccess ?? defaultOnSuccess,
      additionalFormData,
      getAdditionalFormData,
      pushSubmitEvent: () => pushCustomSubmitEvent(id),
      onResponse: () => {
        if (inModal) {
          closeModal();
        }
      },
    },
  })(observer(Form));

  return <Component />;
}

function getInitialValue() {
  if (typeof window !== 'undefined') {
    const country = getCountryCode()?.toLowerCase();
    return phoneCode[country] ?? '+';
  }
  return '+';
}
